import React, { ReactNode, SyntheticEvent, useState } from 'react';

import { Breadcrumbs, Card, CardContent, Grid, Paper, Skeleton, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Moment from 'moment';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { useContainer } from 'unstated-next';

import ClassSessionContainer from './ClassSessionContainer';
import { ClassSessionDetailAI } from './ClassSessionDetailAI';
import { ClassSessionDetailContent } from './ClassSessionDetailContent';
import { ClassSessionDetailInsights } from './ClassSessionDetailInsights';
import { ClassSessionDetailLog } from './ClassSessionDetailLog';
import { ClassSessionDetailStudy } from './ClassSessionDetailStudy';
import { ClassSessionDetailTimeline } from './ClassSessionDetailTimeline';
import { ClassSessionDetailUsers } from './ClassSessionDetailUsers';
import { IsAnyProdEnvironment } from '../../../../../utils/environment';
import AdminAuthContainer from '../../../AdminAuthContainer';
import AdminSettingsContainer from '../../../AdminSettingsContainer';
import { ClassSessionProvider } from '../../../ClassSessionContext';
import StyledFormTitle from '../../../styles/form-title';
import { ClassSessionUsersProvider } from '../ClassSessionUsersContext';

interface TabPanelProps {
    children?: ReactNode;
    dir?: string;
    index: number;
    value: number;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`classSession-detail-tabpanel-${index}`}
            aria-labelledby={`classSession-detail-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ mt: 3 }}>{children}</Box>}
        </div>
    );
}

function a11yProps(index: number) {
    return {
        id: `classSession-detail-tab-${index}`,
        'aria-controls': `classSession-detail-tabpanel-${index}`,
        value: index
    };
}

const Title = styled(StyledFormTitle)`
    margin-bottom: 1rem;
`;

const Spacer = styled.div`
    margin: 1rem;
`;

const PaddedSkeleton = styled(Skeleton)`
    margin: 0.5rem 0;
`;

export const ClassSessionDetail = () => {
    const { t } = useTranslation(['portal', 'create_edit_classSession']);
    const { classSessionData } = useContainer(ClassSessionContainer);
    const { authMode } = useContainer(AdminAuthContainer);
    const { adminSettings } = useContainer(AdminSettingsContainer);
    const theme = useTheme();
    const [tabValue, setTabValue] = useState(0);

    const handleTabChange = (event: SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
    };
    if (!classSessionData || classSessionData.loading) {
        return (
            <>
                <Grid container justifyContent="space-between">
                    <Grid item>
                        <Skeleton width={300} height={60} />
                    </Grid>
                    <Grid item>
                        <Skeleton width={150} height={60} />
                    </Grid>
                </Grid>
                <Spacer />
                <PaddedSkeleton height={35} />
                <PaddedSkeleton height={35} />
                <PaddedSkeleton height={35} />
                <PaddedSkeleton height={35} />
                <PaddedSkeleton height={35} />
                <Spacer />
                <Grid container justifyContent="flex-end">
                    <Skeleton width={400} height={50} />
                </Grid>
            </>
        );
    }

    return (
        <div style={{ fontFamily: 'Lato' }}>
            <Grid container justifyContent="space-between">
                <Grid item>
                    <Title>{t('portal:classSessions.title')}</Title>
                </Grid>
            </Grid>

            <Breadcrumbs aria-label="breadcrumb">
                <Link to="/portal/classes">{t('portal:classSessions.all-classes')}</Link>
                <Link to={`/portal/classes/${classSessionData?.class.class_uuid}`}>
                    {classSessionData?.class.class_name}
                </Link>
                <Typography color="text.primary">
                    {Moment(new Date(classSessionData?.date_str)).format('MMMM Do YYYY')}
                </Typography>
            </Breadcrumbs>

            <Paper elevation={0} sx={{ mt: 2 }}>
                <Tabs
                    value={tabValue}
                    onChange={handleTabChange}
                    indicatorColor="primary"
                    textColor="primary"
                    variant="fullWidth"
                    aria-label={t('portal:classSessions.classSession-detail-tabs')}
                >
                    <Tab label={t('portal:classSessions.timeline')} {...a11yProps(0)} />
                    {['customerSupport'].includes(authMode) && IsAnyProdEnvironment() && (
                        <Tab label={t('portal:classSessions.content')} {...a11yProps(1)} />
                    )}
                    {false && <Tab label={t('portal:classSessions.insights')} {...a11yProps(2)} />}
                    {['customerSupport'].includes(authMode) && IsAnyProdEnvironment() && (
                        <Tab label={t('portal:classSessions.log')} {...a11yProps(3)} />
                    )}
                    {['customerSupport'].includes(authMode) && adminSettings.featureSettings.allowAiForSupport && (
                        <Tab label={t('portal:classSessions.ai-analysis')} {...a11yProps(4)} />
                    )}
                    {['customerSupport'].includes(authMode) && (
                        <Tab label={t('portal:classSessions.users')} {...a11yProps(5)} />
                    )}
                    {['student', 'instructor'].includes(authMode) && (
                        <Tab label={t('portal:classSessions.study')} {...a11yProps(6)} />
                    )}
                </Tabs>
            </Paper>

            <ClassSessionProvider classSessionUuid={classSessionData?.class_session_uuid || ''}>
                <TabPanel value={tabValue} index={0} dir={theme.direction}>
                    <Card elevation={0} sx={{ overflow: 'visible', padding: 0 }}>
                        <CardContent>{classSessionData && <ClassSessionDetailTimeline />}</CardContent>
                    </Card>
                </TabPanel>
                <TabPanel value={tabValue} index={1} dir={theme.direction}>
                    <Card elevation={0} sx={{ overflow: 'visible', padding: 0 }}>
                        <CardContent>{classSessionData && <ClassSessionDetailContent />}</CardContent>
                    </Card>
                </TabPanel>
                <TabPanel value={tabValue} index={2} dir={theme.direction}>
                    <Card elevation={0} sx={{ overflow: 'visible', padding: 0 }}>
                        <CardContent>{classSessionData && <ClassSessionDetailInsights />}</CardContent>
                    </Card>
                </TabPanel>
                <TabPanel value={tabValue} index={3} dir={theme.direction}>
                    <Card elevation={0} sx={{ overflow: 'visible', padding: 0 }}>
                        <CardContent>{classSessionData && <ClassSessionDetailLog />}</CardContent>
                    </Card>
                </TabPanel>
                <TabPanel value={tabValue} index={4} dir={theme.direction}>
                    <Card elevation={0} sx={{ overflow: 'visible', padding: 0 }}>
                        <CardContent>{classSessionData && <ClassSessionDetailAI />}</CardContent>
                    </Card>
                </TabPanel>
                <TabPanel value={tabValue} index={5} dir={theme.direction}>
                    <Card elevation={0} sx={{ overflow: 'visible', padding: 0 }}>
                        <CardContent>
                            {classSessionData && (
                                <ClassSessionUsersProvider classSessionUuid={classSessionData.class_session_uuid}>
                                    <ClassSessionDetailUsers />
                                </ClassSessionUsersProvider>
                            )}
                        </CardContent>
                    </Card>
                </TabPanel>
                <TabPanel value={tabValue} index={6} dir={theme.direction}>
                    <Card elevation={0} sx={{ overflow: 'visible', padding: 0 }}>
                        <CardContent>{classSessionData && <ClassSessionDetailStudy />}</CardContent>
                    </Card>
                </TabPanel>
            </ClassSessionProvider>
        </div>
    );
};
